import React from 'react';
import {
  Button, Switch, Popconfirm, Space, Tag, Tooltip,
} from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { DragHandle } from './AutoUploadRulesTable';
import documentDefaultLabel from './autoUploadRuleDocumentDefaults';

const ACTION_LABELS = {
  upload: 'Upload to EMR',
  upload_and_task: 'Upload + create task',
  skip: 'Skip (no upload)',
  skip_with_failed_row: 'Skip (record failure)',
};

const ARCHIVE_SCOPE_LABELS = {
  inbox_only: 'Inbox only',
  rm_only: 'Referrals only',
  both: 'All documents',
};

const CONDITION_KIND_LABELS = {
  label: 'label',
  caller_name: 'caller',
  from_number: 'from number',
  sender_email: 'sender email',
};

const CONDITION_OPERATOR_LABELS = {
  equals: 'equals',
  contains: 'contains',
  in: 'in',
  not_equals: 'not',
  absent: 'absent',
};

const CONDITION_COUNT_NOUNS = {
  label: 'labels',
  caller_name: 'names',
  from_number: 'numbers',
  sender_email: 'emails',
};

const sameSourceScope = (earlier, rule) => (
  !earlier.source_type
  || (earlier.source_type === rule.source_type && earlier.source_id === rule.source_id)
  || (
    earlier.source_type === 'Department'
    && ['FaxNumber', 'Email'].includes(rule.source_type)
    && earlier.source_id === rule.source_department_id
  )
);

const shadowingRule = (rules, rule, index) => rules.slice(0, index).find((earlier) => (
  earlier.active
  && !(earlier.action === 'upload_and_task' && !earlier.task_routing_rule_id)
  && sameSourceScope(earlier, rule)
  && earlier.category_match_mode === 'any'
  && (earlier.categories || []).length === 0
  && (earlier.conditions || []).length === 0
)) || null;

const describeCondition = (condition) => {
  const kind = CONDITION_KIND_LABELS[condition.kind] || condition.kind;
  const operator = CONDITION_OPERATOR_LABELS[condition.operator] || condition.operator;
  let value = '';
  if (condition.operator === 'in' && Array.isArray(condition.value)) {
    const noun = CONDITION_COUNT_NOUNS[condition.kind] || 'values';
    value = `${condition.value.length} ${noun}`;
  } else if (Array.isArray(condition.value)) {
    value = condition.value.map((entry) => `"${entry}"`).join(', ');
  } else if (condition.value !== null && condition.value !== undefined && condition.value !== '') {
    value = `"${condition.value}"`;
  }
  return [kind, operator, value].filter(Boolean).join(' ');
};

const describeConditions = (rule) => {
  const conditions = rule.conditions || [];
  if (conditions.length === 0) return '—';
  const connector = rule.condition_match_mode === 'any' ? 'or' : 'and';
  return (
    <Space size={4} wrap>
      {conditions.map((condition, index) => (
        <React.Fragment key={condition.id}>
          <Tag>{describeCondition(condition)}</Tag>
          {index < conditions.length - 1 && (
            <span style={{ color: '#888', fontSize: 12 }}>{connector}</span>
          )}
        </React.Fragment>
      ))}
    </Space>
  );
};

const describeCategories = (rule) => {
  const names = (rule.categories || []).map((category) => category.emr_category);
  if (rule.category_match_mode === 'all_except') {
    return names.length ? `All except: ${names.join(', ')}` : 'All';
  }
  return names.length ? `Only: ${names.join(', ')}` : 'All categories';
};

const describeDocumentDefaults = (rule) => {
  const defaults = rule.document_defaults_labels || rule.document_defaults || {};
  const summary = Object.entries(defaults).map(([key, value]) => {
    const label = documentDefaultLabel(key);
    if (typeof value === 'boolean') return `${label}: ${value ? 'Yes' : 'No'}`;
    return key === 'notes' ? label : `${label} ${value}`;
  }).join(' · ');
  if (!summary) return '—';

  return (
    <Tooltip title={summary}>
      <span>{summary.length > 56 ? `${summary.slice(0, 53)}...` : summary}</span>
    </Tooltip>
  );
};

const describeSource = (rule) => {
  if (!rule.source_type) return <Tag>Org-wide</Tag>;
  let tag;
  if (rule.source_type === 'FaxNumber') tag = <Tag color="blue">Fax line</Tag>;
  else if (rule.source_type === 'Email') tag = <Tag color="purple">Email</Tag>;
  else if (rule.source_type === 'Department') tag = <Tag color="cyan">Department</Tag>;
  else tag = <Tag>{rule.source_type}</Tag>;
  const deletedDepartment = rule.source_type === 'Department' && rule.source_label === 'Department (deleted)';
  return (
    <Space direction="vertical" size={0}>
      {tag}
      {rule.source_label && (
        <span style={{ fontSize: 12, color: '#888' }}>
          {deletedDepartment ? (
            <>
              Department
              {' '}
              <span style={{ color: '#bbb' }}>(deleted)</span>
            </>
          ) : rule.source_label}
        </span>
      )}
    </Space>
  );
};

const buildAutoUploadRuleColumns = ({
  isAdmin, rules, onEdit, onDelete, onToggleActive,
}) => {
  const columns = [];
  if (isAdmin) {
    columns.push({
      title: '', key: 'drag', width: 40, align: 'center', render: () => <DragHandle />,
    });
  }
  columns.push(
    {
      title: 'Priority', dataIndex: 'priority', key: 'priority', width: 90,
    },
    {
      title: 'Name',
      key: 'name',
      render: (_, rule, index) => {
        const shadower = shadowingRule(rules, rule, index);
        return (
          <Space size={4}>
            {rule.name || <em>Unnamed</em>}
            {shadower && (
              <Tooltip title={`This rule can never match — rule "${shadower.name || 'Unnamed'}" above it matches every document first.`}>
                <Tag icon={<WarningOutlined />} color="warning">Shadowed</Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    { title: 'Applies to', key: 'source', render: (_, rule) => describeSource(rule) },
    { title: 'Action', key: 'action', render: (_, rule) => ACTION_LABELS[rule.action] || rule.action },
    { title: 'Categories', key: 'categories', render: (_, rule) => describeCategories(rule) },
    { title: 'Defaults', key: 'defaults', render: (_, rule) => describeDocumentDefaults(rule) },
    { title: 'Conditions', key: 'conditions', render: (_, rule) => describeConditions(rule) },
    {
      title: 'Archive',
      key: 'archive',
      render: (_, rule) => (rule.auto_archive
        ? <Tag color="green">{ARCHIVE_SCOPE_LABELS[rule.auto_archive_scope] || rule.auto_archive_scope}</Tag>
        : '—'),
    },
    {
      title: 'Active',
      key: 'active',
      width: 90,
      render: (_, rule) => (
        <Switch checked={rule.active} disabled={!isAdmin} onChange={() => onToggleActive(rule)} />
      ),
    },
  );
  if (isAdmin) {
    columns.push({
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 150,
      render: (_, rule) => (
        <Space>
          <Button size="small" onClick={() => onEdit(rule)}>Edit</Button>
          <Popconfirm title="Delete this rule?" onConfirm={() => onDelete(rule)}>
            <Button size="small" danger>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    });
  }
  return columns;
};

export { buildAutoUploadRuleColumns, describeDocumentDefaults, shadowingRule };
